import { getOrderedLessonsFromStream, getComingSoonLessonLocalePackIds, type StreamForDashboard } from 'Lessons';
import { useContentProgress, getLessonProgressStatus } from 'ContentProgress';
import { useLaunchableLessonsLocalePackIdsByStream } from 'Streams';

export const useNextUpLesson = (stream?: StreamForDashboard) => {
    const { lessonProgressByLocalePackId } = useContentProgress();
    const lessons = getOrderedLessonsFromStream(stream);
    const launchableLessonLocalePackIds = useLaunchableLessonsLocalePackIdsByStream(stream);
    const comingSoonLessonLocalePackIds = getComingSoonLessonLocalePackIds(stream);
    const completedLessonLocalePackIds = lessons
        .filter(l => getLessonProgressStatus(lessonProgressByLocalePackId[l.localePackId]) === 'completed')
        .map(lesson => lesson.localePackId);

    /*
        There are a couple of surprising things here:
        1. comingSoon lessons are always unlaunchable. So that check is unnecessary
        2. If the only incomplete lesson is unlaunchable, it will be assigned as the next up lesson. This can
            happen if all lessons except for the smartcase are complete, and the user is offline. In that case,
            the user will see the smartcase as the nextUp lesson, click on it, and get a message saying the lesson
            cannot be launched when offline.
    */
    const nextUpLesson = lessons.reduce(
        (prev, curr) => {
            if (completedLessonLocalePackIds.includes(curr.localePackId)) return prev; // Skip completed lessons
            if (comingSoonLessonLocalePackIds.includes(curr.localePackId)) return prev; // Skip coming soon lessons
            if (!launchableLessonLocalePackIds.includes(curr.localePackId)) return prev; // Skip un-launchable lessons

            const prevLessonProgress = lessonProgressByLocalePackId[prev.localePackId];
            const currLessonProgress = lessonProgressByLocalePackId[curr.localePackId];

            // If the current lesson has no progress return the previous lesson
            // This gives priority to earlier lessons
            if (!currLessonProgress?.lastProgressAt) return prev;

            // If the current lesson has progress and the previous lesson does not, return the current lesson
            if (currLessonProgress.lastProgressAt && !prevLessonProgress?.lastProgressAt) return curr;

            // Prefer the lesson that was more recently accessed
            if (currLessonProgress.lastProgressAt > (prevLessonProgress?.lastProgressAt ?? 0)) return curr;

            return prev;
        },
        lessons.find(l => !completedLessonLocalePackIds.includes(l.localePackId)) ?? lessons[0],
    ); // Default to first non-completed lesson in stream

    return {
        nextUpLesson,
        hasProgress: !!lessonProgressByLocalePackId[nextUpLesson?.localePackId],
    };
};
